/**
 * Segment arithmetic behind the four-box IPv4 field — the pure half, kept apart
 * from the component so the paste and edit rules can be exercised directly.
 *
 * The rule these encode: dots belong to the field's chrome, never to a segment.
 * Anything arriving with dots in it (a pasted address, a typed separator) is
 * split on them, and each piece lands in its own box.
 *
 * Contains:
 * - OCTET_COUNT / MAX_OCTET_DIGITS — the shape of an address
 * - toOctets — dotted quad → fixed-length segment array
 * - toValue — segment array → dotted quad, or '' when wholly empty
 * - distributePaste — spread pasted text across segments from a starting box
 *
 * Returns plain JS — no JSX.
 */

export const OCTET_COUNT = 4;
export const MAX_OCTET_DIGITS = 3;

const digitsOnly = text => String(text ?? '').replace(/\D/g, '');

/**
 * @param {string} value - Dotted quad, possibly partial or empty.
 * @returns {string[]} Exactly OCTET_COUNT segments, digits only, '' where absent.
 */
export const toOctets = value => {
  const parts = String(value ?? '').split('.');
  return Array.from({ length: OCTET_COUNT }, (_, i) => digitsOnly(parts[i]).slice(0, MAX_OCTET_DIGITS));
};

/**
 * @param {string[]} octets - Segment values.
 * @returns {string} The joined dotted quad, or '' when no segment holds digits.
 */
export const toValue = octets => (octets.every(octet => octet === '') ? '' : octets.join('.'));

/**
 * Spread pasted text across the segments, starting at the focused box.
 *
 * @param {string[]} octets - Current segment values.
 * @param {number}   index  - Segment the paste landed in.
 * @param {string}   pasted - Raw clipboard text, dots and all.
 * @returns {{octets: string[], focusIndex: number}} New segments (the input is
 *   never mutated) and the segment that should end up focused.
 */
export const distributePaste = (octets, index, pasted) => {
  const segments = String(pasted ?? '')
    .trim()
    .split('.')
    .map(segment => digitsOnly(segment).slice(0, MAX_OCTET_DIGITS));
  const next = octets.map((octet, i) => {
    const offset = i - index;
    if (offset < 0 || offset >= segments.length) return octet;
    return segments[offset];
  });
  return {
    octets: next,
    focusIndex: Math.min(index + segments.length - 1, OCTET_COUNT - 1),
  };
};
